import { useState, useEffect } from 'react';
import KitModal from '../../../UI/KitModal';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Switch,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import { Info, Plus, Trash } from '@phosphor-icons/react';
import { useAuthStore } from '../../../../store/storeAuth';
import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';
import { useObjectStore } from '../../../../store/storeObjects';
import { CompanySpacesService } from '../../../../service/CompanySpacesService';
import { UserService } from '../../../../service/UserService';
import { AuthService } from '../../../../service/AuthService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useDarkMode } from '../../../../hooks/useDarkMode';

const ObjectModal = ({
  show,
  onHide,
  flowId = '',
  spaceId: initialSpaceId = 'user',
  selectObject = () => {},
}: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [spaceId, setSpaceId] = useState<string>(initialSpaceId);
  const [spaces, setSpaces] = useState<any[]>([]);

  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const { user, setAuthUser } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    objectUpdate,
    setObjectUpdate,
    addObject,
    updateObject,
    getObjectsBySpaceId,
  } = useObjectStore(state => state);

  useEffect(() => {
    if (show) {
      setSpaceId(initialSpaceId);
      loadSpaces();
    }
  }, [show, initialSpaceId]);

  const loadSpaces = async () => {
    try {
      const { data } = await UserService.getSpaces();
      setSpaces(data);
    } catch (error) {
      console.error('Failed to load spaces:', error);
    }
  };

  const onObjectChange = (e: {
    target: { name: any; value: any; checked?: any };
  }) => {
    if (e.target.name === 'objectName') {
      setObjectUpdate({ ...objectUpdate, name: e.target.value });
    } else if (e.target.name === 'objectLocal') {
      const isLocal = !objectUpdate.localObject;
      setObjectUpdate({
        ...objectUpdate,
        localObject: isLocal,
        flowId: isLocal ? flowId : '',
      });
    } else {
      const modifiedObjectTexts = objectUpdate.text.map((text: any) => {
        if (e.target.name === text.id) {
          return {
            ...text,
            text: e.target.value,
          };
        }
        return text;
      });
      setObjectUpdate({ ...objectUpdate, text: modifiedObjectTexts });
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);

    if (objectUpdate?.isCreating) {
      const existingObjects = getObjectsBySpaceId(spaceId);
      const objectExists = existingObjects.some(obj => obj.id === objectUpdate.id);
      if (!objectExists) {
        addObject(objectUpdate, spaceId);
      } else {
        updateObject(objectUpdate.id, objectUpdate, spaceId);
      }
    } else {
      updateObject(objectUpdate.id, objectUpdate, spaceId);
    }

    const objects = getObjectsBySpaceId(spaceId);

    if (spaceId === 'user') {
      await UserService.updateUser({ objects });

      const userRes = await AuthService.getMe();
      setAuthUser(userRes?.data);
    } else {
      await CompanySpacesService.updateCompanySpace(user?.selectedCompanyId!, {
        spaceId,
        objects,
      });
    }

    objectUpdate.isCreating
      ? setNotification({
          type: NotificationType.Success,
          message: 'Object created successfully',
        })
      : setNotification({
          type: NotificationType.Success,
          message: 'Object updated successfully',
        });

    window.dispatchEvent(new CustomEvent("sidebar-refresh"));

    setIsLoading(false);
    selectObject(objectUpdate.id);
    onHide();
  };

  const addNewElement = () => {
    const newElement = {
      id: Math.random().toString(36).substr(2, 9),
      text: '',
    };

    setObjectUpdate({
      ...objectUpdate,
      text: [...objectUpdate.text, newElement],
    });
  };

  const onRemoveElement = (id: string) => {
    const modifiedObjectTexts = objectUpdate.text.filter(
      (text: any) => text.id !== id,
    );
    setObjectUpdate({ ...objectUpdate, text: modifiedObjectTexts });
  };

  return (
    <KitModal
      show={show}
      onHide={onHide}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '650px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      footer={
        <Box>
          <Button onClick={addNewElement} disabled={isLoading}>
            <Plus size={15} style={{ marginRight: '4px' }} /> Add element
          </Button>

          <Button
            disabled={isLoading}
            variant="contained"
            onClick={onSubmit}
            style={{
              marginLeft: '8px',
            }}
          >
            {objectUpdate?.isCreating ? 'Create' : 'Update'}
          </Button>
        </Box>
      }
    >
      <Box>
        <Typography
          variant="h6"
          color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
        >
          {objectUpdate?.isCreating ? 'Create Object' : 'Update Object'}
        </Typography>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          my={'16px'}
          borderRadius={'4px'}
        >
          <FormControl fullWidth>
            <InputLabel>Select Space</InputLabel>
            <Select
              value={spaceId}
              onChange={(e) => setSpaceId(e.target.value)}
              label="Select Space"
              disabled={isLoading || !!flowId}
              sx={{
                backgroundColor: isDarkMode ? palette.gray[800] : '#FFFFFF',
                color: isDarkMode ? palette.gray.A100 : palette.gray[900],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? palette.gray[700] : palette.gray[300],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? palette.gray[600] : palette.gray[400],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.blue[500],
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? palette.gray[400] : palette.gray[600],
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: palette.blue[500],
                },
              }}
            >
              <MenuItem value="user">
                <em>Private</em>
              </MenuItem>
              {spaces.map((space) => (
                <MenuItem 
                  key={space.id} 
                  value={space.id}
                  sx={{
                    color: isDarkMode ? palette.gray.A100 : palette.gray[900],
                    '&:hover': {
                      backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
                    },
                  }}
                >
                  {space.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            name="objectName"
            placeholder="Enter object's name"
            value={objectUpdate.name}
            onChange={onObjectChange}
            fullWidth
            variant="outlined"
            label="Object's Name"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? palette.gray[800] : '#FFFFFF',
                color: isDarkMode ? palette.gray.A100 : palette.gray[900],
              },
            }}
          />
        </Box>

        <Divider sx={{ mb: '20px' }} />
        {objectUpdate?.text?.map((item: any) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '20px',
                width: '100%',
                alignContent: 'center',
                mb: 2,
              }}
              key={item?.id}
              className="input-group-prepend"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: '20px',
                }}
              >
                <textarea
                  className="nowheel nodrag"
                  placeholder="Enter element's value"
                  value={item?.text}
                  name={item.id}
                  onChange={onObjectChange}
                  style={{
                    maxWidth: '100%',
                    width: '100%',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0',
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'inherit',
                    color: '#000000',
                    resize: 'vertical',
                  }}
                />

                <IconButton
                  color={'error'}
                  onClick={() => onRemoveElement(item?.id)}
                >
                  <Trash size={20} />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        {flowId && (
          <Box>
            <Typography
              variant="h6"
              color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            >
              Object Type
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <IconButtonTooltip
                title={
                  'Local setting means this object can only be seen, selected, and used within this agent. It will not be visible in any other agent or on the left-navigation space.'
                }
              >
                <Typography color="#98A2B3" fontSize={'14px'}>
                  Local
                </Typography>
                <Info color="#98A2B3" size={12} style={{ marginLeft: '4px' }} />
              </IconButtonTooltip>
              <Switch
                name="objectLocal"
                size="small"
                onChange={(e: any) => onObjectChange(e)}
                checked={!objectUpdate.localObject}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#1976d2',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#1976d2',
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#1976d2',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#1976d2',
                    opacity: 0.5,
                  },
                }}
              />
              <IconButtonTooltip
                title={
                  'Global setting means this object can be used or referenced by any and all agents within this space.'
                }
              >
                <Typography color="#98A2B3" fontSize={'14px'}>
                  Global
                </Typography>
                <Info color="#98A2B3" size={12} style={{ marginLeft: '4px' }} />
              </IconButtonTooltip>
            </Box>
          </Box>
        )}
      </Box>
    </KitModal>
  );
};

export default ObjectModal;
